import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { DollarSign, Loader2, Receipt, Inbox } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { reimbursementAPI } from '@/lib/api';
import ReimbursementDetailDialog from '@/components/reimbursements/ReimbursementDetailDialog';
import { CATEGORY_LABEL, STATUS_META, money } from '@/components/reimbursements/constants';

const FILTERS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'under_review', label: 'In review' },
  { key: 'approved', label: 'Approved' },
  { key: 'paid', label: 'Paid' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

export default function ReimbursementsAdminPage() {
  const [filter, setFilter] = useState('submitted');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await reimbursementAPI.list(filter === 'all' ? undefined : filter);
      setRequests(r.data.requests || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load reimbursements');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const onChanged = (updated) => {
    setSelected(updated);
    load();
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-5" data-testid="reimbursements-admin-page">
      <div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900 flex items-center gap-2">
          <DollarSign size={22} className="text-emerald-600" /> Reimbursements
        </h1>
        <p className="text-sm text-slate-500">Review agent reimbursement requests. Approving sends the payment into the Payments → Payment Batches pipeline.</p>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="reimbursement-filters">
        {FILTERS.map((f) => {
          const active = filter === f.key;
          const n = f.key === 'all' ? null : counts[f.key];
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-sm rounded-full px-3 py-1.5 border transition ${active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              data-testid={`reimbursement-filter-${f.key}`}
            >
              {f.label}{n ? <span className={`ml-1.5 ${active ? 'text-white/80' : 'text-slate-400'}`}>{n}</span> : ''}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : requests.length === 0 ? (
        <Card><CardContent className="py-16 text-center text-slate-500">
          <Inbox size={32} className="mx-auto text-slate-300 mb-2" />
          <p className="text-sm">No requests in this view.</p>
        </CardContent></Card>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Agent</th>
                  <th className="px-4 py-2.5 font-semibold">Title</th>
                  <th className="px-4 py-2.5 font-semibold">Type</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Amount</th>
                  <th className="px-4 py-2.5 font-semibold text-center">Receipts</th>
                  <th className="px-4 py-2.5 font-semibold">Submitted</th>
                  <th className="px-4 py-2.5 font-semibold text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {requests.map((r) => {
                  const meta = STATUS_META[r.status] || {};
                  return (
                    <tr key={r.request_id} className="hover:bg-slate-50/70 cursor-pointer" onClick={() => setSelected(r)} data-testid={`reimbursement-row-${r.request_id}`}>
                      <td className="px-4 py-2.5 font-medium text-slate-800">{r.requester_name}</td>
                      <td className="px-4 py-2.5 text-slate-700">{r.title}</td>
                      <td className="px-4 py-2.5 text-slate-500">{CATEGORY_LABEL[r.category] || r.category}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-emerald-700">{money(r.amount)}</td>
                      <td className="px-4 py-2.5 text-center text-slate-500"><span className="inline-flex items-center gap-1"><Receipt size={12} /> {(r.receipts || []).length}</span></td>
                      <td className="px-4 py-2.5 text-slate-500 text-xs">{r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}</td>
                      <td className="px-4 py-2.5 text-right"><Badge className={`text-[10px] border ${meta.cls || ''}`}>{meta.label || r.status}</Badge></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ReimbursementDetailDialog
        open={!!selected}
        onOpenChange={(v) => { if (!v) setSelected(null); }}
        request={selected}
        admin
        onChanged={onChanged}
      />
    </div>
  );
}
